import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
    ChangeEvent,
    ReactNode,
} from "react";
import DaumPostcode from "react-daum-postcode";

import style from "../style/components/Inputs.module.css";

declare global {
    interface Window {
        naver?: any;
    }
}

const BlockInput = forwardRef(
    (
        {
            type = "text",
            placeholder,
            disabled,
            value,
            setValue,
            onChange,
            error,
            requiredMark,
            rightElement,
        }: {
            type?: "text" | "number" | "password";
            placeholder: string;
            disabled?: boolean;
            value?: string;
            setValue: (value: string) => void;
            onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
            error?: string;
            requiredMark?: boolean;
            rightElement?: ReactNode;
        },
        ref
    ) => {
        const input = useRef<HTMLInputElement | null>(null);

        useImperativeHandle(ref, () => ({
            focus: () => input.current?.focus(), // 부모에서 focus 호출 가능
        }));

        return (
            <div>
                <div className={[style["container"], error && style["container--error"]].join(" ")}>
                    <input
                        ref={input}
                        onChange={(e) => {
                            if (onChange) onChange(e);
                            setValue(e.target.value);
                        }}
                        className={[style["input--default"], rightElement && style["input--default--with-action"]].join(" ")}
                        type={type}
                        value={value || ""}
                        disabled={disabled ? true : false}
                        placeholder=" "
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                    />

                    <span className={style["span--input--default"]}>
                        {placeholder}
                        {requiredMark && <em className={style["mark--required"]}>*</em>}
                    </span>
                    {rightElement && <div className={style["input-right-action"]}>{rightElement}</div>}
                </div>

                <p className={[style["text--error"], error && style["text--error--active"]].join(" ")}>{error}</p>
            </div>
        );
    }
);

const SelectorInput = forwardRef(
    (
        {
            placeholder,
            value,
            setValue,
            onChange,
            error,
            requiredMark,
            emptyLabel,
            disabled,
            children,
        }: {
            placeholder: string;
            value?: string;
            setValue: (value: string) => void;
            onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
            error?: string;
            requiredMark?: boolean;
            emptyLabel?: string;
            disabled?: boolean;
            children?: ReactNode;
        },
        ref
    ) => {
        const select = useRef<HTMLSelectElement | null>(null);

        useImperativeHandle(ref, () => ({
            focus: () => select.current?.focus(), // 부모에서 focus 호출 가능
        }));

        return (
            <div>
                <div className={[style["container"], error && style["container--error"]].join(" ")}>
                    <select
                        ref={select}
                        onChange={(e) => {
                            if (onChange) onChange(e);
                            setValue(e.target.value);
                        }}
                        className={style["select--default"]}
                        value={value || ""}
                        disabled={disabled ? true : false}
                        required
                    >
                        <option value="">{emptyLabel || ""}</option>
                        {children}
                    </select>

                    <span className={style["span--select--default"]}>
                        {placeholder}
                        {requiredMark && <em className={style["mark--required"]}>*</em>}
                    </span>

                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 12.83 7"
                        fill="#919191"
                        className={style["select--default--svg"]}
                    >
                        <path d="M12.54,.39c-.43-.48-1.17-.52-1.65-.1l-4.47,3.98L1.94,.29C1.46-.13,.72-.09,.29,.39s-.38,1.22,.1,1.65L5.64,6.71c.21,.18,.48,.29,.78,.29s.57-.11,.78-.29h0S12.44,2.04,12.44,2.04c.48-.43,.53-1.17,.1-1.65Z" />
                    </svg>
                </div>

                <p className={[style["text--error"], error && style["text--error--active"]].join(" ")}>{error}</p>
            </div>
        );
    }
);

const AddressInput = ({
    placeholder,
    value,
    setValue,
    onComplete,
    error,
    requiredMark,
}: {
    placeholder: string;
    value?: string;
    setValue: (value: string) => void;
    onComplete?: (data: any) => void;
    error?: string;
    requiredMark?: boolean;
}) => {
    const input = useRef<HTMLInputElement | null>(null);
    const [active, setActive] = useState<boolean>(false);

    const setAddressData = (data: any) => {
        setActive(false);
        if (onComplete) {
            onComplete(data);
            return;
        }
        setValue(data.address);
    };

    return (
        <>
            <div>
                <button onClick={() => setActive(true)} className={style["button--address-input"]}>
                    <div className={[style["container"], error && style["container--error"]].join(" ")}>
                        <input
                            ref={input}
                            onClick={() => setActive(true)}
                            className={[style["input--default"], style["input--default--address"]].join(" ")}
                            type="text"
                            value={value}
                            readOnly={true}
                            placeholder=" "
                            autoComplete="off"
                            autoCapitalize="off"
                            autoCorrect="off"
                            spellCheck="false"
                        />

                        <span className={style["span--input--default"]}>
                            {placeholder}
                            {requiredMark && <em className={style["mark--required"]}>*</em>}
                        </span>
                    </div>
                </button>

                <p className={[style["text--error"], error && style["text--error--active"]].join(" ")}>{error}</p>
            </div>

            {active && (
                <div className={style["container--address-input"]}>
                    <DaumPostcode className={style["daum-postcode"]} onComplete={setAddressData} />
                </div>
            )}
        </>
    );
};

interface NaverAddressResult {
    roadAddress?: string;
    jibunAddress?: string;
    englishAddress?: string;
    x: string;
    y: string;
}

const NaverAddressInput = ({
    placeholder,
    value,
    setValue,
    setCoordinates,
    error,
    requiredMark,
}: {
    placeholder: string;
    value?: string;
    setValue: (value: string) => void;
    setCoordinates: (coordinates: { lat: number; lng: number }) => void;
    error?: string;
    requiredMark?: boolean;
}) => {
    const input = useRef<HTMLInputElement | null>(null);
    const [active, setActive] = useState<boolean>(false);
    const [query, setQuery] = useState<string>(value || "");
    const [results, setResults] = useState<NaverAddressResult[]>([]);
    const [searching, setSearching] = useState<boolean>(false);
    const [searchError, setSearchError] = useState<string>("");

    useEffect(() => {
        if (!active) {
            setQuery(value || "");
            setResults([]);
            setSearchError("");
        }
    }, [active, value]);

    const search = () => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) {
            setSearchError("검색할 주소를 입력해주세요.");
            return;
        }

        const service = window.naver?.maps?.Service;
        if (!service?.geocode) {
            setSearchError("네이버 주소검색을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
            return;
        }

        setSearching(true);
        setSearchError("");
        service.geocode({ query: trimmedQuery }, (status: any, response: any) => {
            setSearching(false);

            if (status !== service.Status.OK) {
                setResults([]);
                setSearchError("주소를 검색하지 못했습니다. 검색어를 확인해주세요.");
                return;
            }

            const addresses = (response?.v2?.addresses || []) as NaverAddressResult[];
            const validResults = addresses.filter((address) => {
                const lat = Number(address.y);
                const lng = Number(address.x);
                return Number.isFinite(lat) && Number.isFinite(lng);
            });

            setResults(validResults);
            if (validResults.length === 0) {
                setSearchError("검색 결과가 없습니다.");
            }
        });
    };

    const selectAddress = (result: NaverAddressResult) => {
        const selectedAddress = result.roadAddress || result.jibunAddress || "";
        const lat = Number(result.y);
        const lng = Number(result.x);

        if (!selectedAddress || !Number.isFinite(lat) || !Number.isFinite(lng)) {
            setSearchError("선택한 주소의 좌표를 확인하지 못했습니다.");
            return;
        }

        setValue(selectedAddress);
        setCoordinates({ lat, lng });
        setActive(false);
    };

    return (
        <>
            <div>
                <button type="button" onClick={() => setActive(true)} className={style["button--address-input"]}>
                    <div className={[style["container"], error && style["container--error"]].join(" ")}>
                        <input
                            ref={input}
                            onClick={() => setActive(true)}
                            className={[style["input--default"], style["input--default--address"]].join(" ")}
                            type="text"
                            value={value || ""}
                            readOnly={true}
                            placeholder=" "
                            autoComplete="off"
                        />

                        <span className={style["span--input--default"]}>
                            {placeholder}
                            {requiredMark && <em className={style["mark--required"]}>*</em>}
                        </span>
                    </div>
                </button>

                <p className={[style["text--error"], error && style["text--error--active"]].join(" ")}>{error}</p>
            </div>

            {active && (
                <div
                    className={[style["container--address-input"], style["naver-address-input-panel"]].join(" ")}
                >
                    <div className={style["naver-address-search"]}>
                        <input
                            ref={input}
                            className={style["naver-address-search-input"]}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") search();
                            }}
                            placeholder="도로명 또는 지번 주소"
                            autoFocus
                        />
                        <button type="button" onClick={search} disabled={searching} className={style["naver-address-search-button"]}>
                            {searching ? "검색 중" : "검색"}
                        </button>
                        <button type="button" onClick={() => setActive(false)} className={style["naver-address-close-button"]}>
                            닫기
                        </button>
                    </div>

                    {searchError && <p className={style["naver-address-search-error"]}>{searchError}</p>}

                    <div className={style["naver-address-results"]}>
                        {results.map((result, index) => {
                            const primary = result.roadAddress || result.jibunAddress || "주소 없음";
                            const secondary = result.roadAddress && result.jibunAddress && result.roadAddress !== result.jibunAddress
                                ? result.jibunAddress
                                : result.englishAddress;

                            return (
                                <button
                                    type="button"
                                    key={`${result.x}-${result.y}-${index}`}
                                    className={style["naver-address-result"]}
                                    onClick={() => selectAddress(result)}
                                >
                                    <strong>{primary}</strong>
                                    {secondary && <small>{secondary}</small>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

const ToggleSwitch = ({
    placeholder,
    value,
    checked,
    disabled,
    onChange,
}: {
    placeholder?: string;
    value?: string;
    checked: boolean;
    disabled?: boolean;
    onChange: () => void;
}) => {
    const handleOnChange = () => {
        onChange();
    };

    return (
        <div className={style["container--toggle-switch"]}>
            <input
                value={value}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={handleOnChange}
                className={style["input--toggle-switch"]}
            />
            <span className={style["span--toggle-switch"]} style={{ display: !placeholder ? "none" : "" }}>
                {placeholder}
            </span>
        </div>
    );
};

const PhotoInput = ({
    photo,
    setPhoto,
    progress = 0,
    error,
    existingImageUrl,
}: {
    photo: File | null;
    setPhoto: (file: File) => void;
    progress?: number;
    error: string;
    existingImageUrl?: string; // 기존 이미지 URL 추가
}) => {
    const [clicked, setClicked] = useState<boolean>(false);
    const [hovered, setHovered] = useState<boolean>(false);
    const [thumbnail, setThumbnail] = useState<string>(existingImageUrl || ""); // 기존 이미지 URL을 초기값으로 사용

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            fileToTumbnail(file);
            setPhoto(file);
        }
    };

    const fileToTumbnail = (f: File) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            setThumbnail(reader.result as string);
        };
        reader.readAsDataURL(f);
    };

    // photo가 변경될 때만 thumbnail 업데이트
    useEffect(() => {
        if (photo) {
            fileToTumbnail(photo);
        } else if (existingImageUrl) {
            setThumbnail(existingImageUrl);
        }
    }, [photo, existingImageUrl]);

    return (
        <div>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => {
                    setHovered(false);
                    setClicked(false);
                }}
                // click events
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["container--photo-input"],
                    clicked && style["container--photo-input--clicked"],
                    progress && style["container--photo-input--progress"],
                    error && style["container--photo-input--error"],
                ].join(" ")}
            >
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }} // input을 숨기기
                    id="file-input"
                />
                <label
                    htmlFor="file-input"
                    className={[style["label--photo-input"], hovered && style["label--photo-input--hover"]].join(" ")}
                ></label>
                <div
                    style={{ backgroundImage: `url(${thumbnail})` }}
                    className={style["unit--photo-input--thumbnail"]}
                />
            </div>

            <p
                className={[
                    style["text--photo-input--progress"],
                    progress > 0 && style["text--photo-input--progress--active"],
                ].join(" ")}
            >
                업로드 중... {Math.round(progress)}%
            </p>

            <p className={[style["text--error"], error && style["text--error--active"]].join(" ")}>{error}</p>
        </div>
    );
};

const photoResize = async (file: File, fileName: string): Promise<File> => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    try {
        await new Promise<void>((resolve, reject) => {
            if (img.complete && img.naturalHeight !== 0) {
                resolve();
            } else {
                img.onload = () => resolve();
                img.onerror = (err) => reject(err);
            }
        });
    } catch (err) {
        console.error(err);
        throw new Error("Failed to load image");
    }

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to get canvas context"); // canvas context가 없을 경우 처리

    ctx.drawImage(img, 0, 0, img.width, img.height);

    return new Promise<File>((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    const resizedFile = new File([blob], fileName, {
                        type: "image/jpeg",
                    });
                    resolve(resizedFile);
                } else {
                    reject(new Error("Failed to convert canvas to blob"));
                }
            },
            "image/jpeg",
            0.7
        ); // quality: 0.7 (70%)
    });
};

const URLToFile = async (url: string, fileName: string): Promise<File> => {
    const response = await fetch(url);
    const blob = await response.blob();
    const mimeType = blob.type || "image/jpeg";

    return new File([blob], fileName, { type: mimeType });
};

export { BlockInput, SelectorInput, AddressInput, NaverAddressInput, ToggleSwitch, PhotoInput, photoResize, URLToFile };
